import {
  Component,
  inject,
  input,
  OnChanges,
  OnDestroy,
  QueryList,
  signal,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TeamsAutoAttendantsService } from '../../../teams-auto-attendants.service';
import {
  E164_REGEX,
  MenuButtonFormComponent,
  MenuButtonRowEntity,
} from '../menu-button-form/menu-button-form.component';
import { NgClass } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TeamsAutoAttendantEditContext } from '../../../teams-auto-attendant-edit.context';
import { UiCallFlow } from '../teams-auto-attendants-call-flow.component';
import { isEqual } from 'lodash';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles } from '../../../../button/button.component';
import {
  AccountType,
  AutoAttendantButtonAction,
  AutoAttendantButtonView,
  DialKeys,
  DialKeysView,
  MicrosoftTeamsCallQueueViewEntityStatus,
} from '../../../../shared/models/generated/ziroModels';
import { ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TeamsAutoAttendantsAccountSearchResource } from '../../../resources/teams-auto-attendants-account-search.resource';

export interface UiDialButton extends AutoAttendantButtonView {
  key: string;
  button: keyof DialKeys;
  label: string;
  icon: string;
  audioFile?: File;
  subLabel?: string;
  audioFileId?: string | null;
}

const ALL_BUTTON_DEFS: Array<{ button: keyof DialKeys; key: string }> = [
  { button: 'button1', key: '1' },
  { button: 'button2', key: '2' },
  { button: 'button3', key: '3' },
  { button: 'button4', key: '4' },
  { button: 'button5', key: '5' },
  { button: 'button6', key: '6' },
  { button: 'button7', key: '7' },
  { button: 'button8', key: '8' },
  { button: 'button9', key: '9' },
  { button: 'button0', key: '0' },
  { button: 'buttonStar', key: '*' },
  { button: 'buttonPound', key: '#' },
];

@Component({
  selector: 'ziro-auto-attendants-menu-buttons',
  imports: [TranslateModule, MenuButtonFormComponent, NgClass, ZiroFormsModule, NgbTooltip],
  templateUrl: './teams-auto-attendants-menu-buttons.component.html',
  styleUrl: './teams-auto-attendants-menu-buttons.component.scss',
  providers: [TeamsAutoAttendantsService],
})
export class TeamsAutoAttendantsMenuButtonsComponent implements OnChanges, OnDestroy {
  private _editContext = inject(TeamsAutoAttendantEditContext);
  private _teamsAutoAttendantService = inject(TeamsAutoAttendantsService);
  private _accountSearchResource = inject(TeamsAutoAttendantsAccountSearchResource);

  @ViewChildren(MenuButtonFormComponent) rowForms!: QueryList<MenuButtonFormComponent>;

  callFlow = input<UiCallFlow>(null);
  tenantId = input.required<string>();
  dialKeys = input<Partial<DialKeys | DialKeysView>>({});
  buttons = signal<UiDialButton[]>([]);
  unusedOpen = signal(false);

  private _expandedKey = signal<string | null>(null);
  private _buttonValidity = signal<Record<string, ZiroFormsValidationState>>({});
  private _trackedFlowId: string | null = null;
  private _subscriptions = new Subscription();
  private _entityValidationResults = new Map<string, { hasValidLicense: boolean; hasPhoneNumber: boolean }>();
  private _queuedValidationKeys = new Set<string>();

  ngOnChanges(changes: SimpleChanges): void {
    const flowId = this.callFlow()?.id ?? null;
    if (flowId !== this._trackedFlowId) {
      this._trackedFlowId = flowId;
      this._buttonValidity.set({});
      this._entityValidationResults.clear();
      this._queuedValidationKeys.clear();
      const cachedButtons = this._editContext.getButtonsForFlow(flowId ?? '');
      if (cachedButtons.length > 0) {
        this.buttons.set(cachedButtons);
      }
    }
    if (changes['dialKeys']) {
      this._syncButtons();
      this._triggerEntityValidation();
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  get configuredButtons(): UiDialButton[] {
    return this.buttons().filter((dialButton) => dialButton.action);
  }

  get unusedKeys(): { button: string; key: string }[] {
    const configuredKeys = new Set(this.configuredButtons.map((b) => b.key));
    return ALL_BUTTON_DEFS.filter(({ key }) => !configuredKeys.has(key));
  }

  isExpanded(key: string): boolean {
    return this._expandedKey() === key;
  }

  toggleRow(key: string): void {
    this._expandedKey.set(this._expandedKey() === key ? null : key);
  }

  toggleUnused(): void {
    this.unusedOpen.set(!this.unusedOpen());
  }

  onRowUpdate(update: ZiroFormsUpdate<MenuButtonRowEntity>): void {
    this._updateButtonValidity(update);
    if (!this._shouldUpdateButton(update)) {
      return;
    }

    const resolvedAudioFile = this._resolveAudioFile(update.new);
    this.buttons.update((current) => this._updateButtons(current, update.new, resolvedAudioFile));

    const flowId = this.callFlow()?.id;
    if (flowId) {
      this._editContext.updateDialKeys(flowId, this.buttons());
    }
  }

  isValid(): boolean {
    const prompt = this.callFlow().playMenuOptionSettings?.prompt;
    if (!prompt) {
      return true;
    }
    return !Object.values(this._buttonValidity()).some((state) => state === ZiroFormsValidationState.INVALID);
  }

  hasError(key: string): boolean {
    return this._buttonValidity()[key] === ZiroFormsValidationState.INVALID;
  }

  get hasAnyUnusedError(): boolean {
    return this.unusedKeys.some(({ key }) => this.hasError(key));
  }

  validateAndShowErrors(): boolean {
    this._preValidateUnrenderedButtons();

    const invalidEntries = Object.entries(this._buttonValidity()).filter(
      ([, state]) => state === ZiroFormsValidationState.INVALID,
    );

    if (invalidEntries.length > 0) {
      this._expandAndFocusInvalidKey(invalidEntries[0][0]);
      return false;
    }

    this.rowForms?.forEach((form) => form.triggerSubmitValidation());
    return this.rowForms?.toArray().every((form) => form.isFormValid()) ?? true;
  }

  private _triggerEntityValidation(): void {
    const flowId = this.callFlow()?.id ?? null;
    for (const btn of this.buttons()) {
      if (
        (btn.action === AutoAttendantButtonAction.USER || btn.action === AutoAttendantButtonAction.RESOURCE_ACCOUNT) &&
        btn.entity?.upn &&
        !this._queuedValidationKeys.has(btn.key)
      ) {
        this._queuedValidationKeys.add(btn.key);
        this._validateButtonEntityInBackground(btn.key, btn.entity.upn, btn.action, flowId);
      }
    }
  }

  private _validateButtonEntityInBackground(
    key: string,
    upn: string,
    action: AutoAttendantButtonAction,
    flowId: string,
  ): void {
    const accountType = action === AutoAttendantButtonAction.USER ? AccountType.USER : AccountType.RESOURCE_ACCOUNT;
    const sub = this._accountSearchResource
      .search(this.tenantId(), upn, accountType)
      .pipe(take(1))
      .subscribe((results) => {
        if (this.callFlow()?.id !== flowId) {
          return;
        }
        const result = results.find((r) => r.userPrincipalName.toLowerCase() === upn.toLowerCase());
        if (!result) {
          return;
        }
        this._entityValidationResults.set(key, {
          hasValidLicense: result.hasValidLicense,
          hasPhoneNumber: result.hasPhoneNumber,
        });

        if (!(key in this._buttonValidity())) {
          const btn = this.buttons().find((b) => b.key === key);
          if (btn) {
            this._buttonValidity.update((current) => ({
              ...current,
              [key]: this._computeButtonValidity(btn),
            }));
          }
        }
      });
    this._subscriptions.add(sub);
  }

  private _preValidateUnrenderedButtons(): void {
    for (const btn of this.buttons()) {
      if (btn.key in this._buttonValidity()) {
        continue;
      }
      this._buttonValidity.update((current) => ({
        ...current,
        [btn.key]: this._computeButtonValidity(btn),
      }));
    }
  }

  private _computeButtonValidity(btn: UiDialButton): ZiroFormsValidationState {
    switch (btn.action) {
      case AutoAttendantButtonAction.USER: {
        if (!btn.entity) {
          return ZiroFormsValidationState.INVALID;
        }
        const result = this._entityValidationResults.get(btn.key);
        if (result && (!result.hasValidLicense || !result.hasPhoneNumber)) {
          return ZiroFormsValidationState.INVALID;
        }
        return ZiroFormsValidationState.VALID;
      }
      case AutoAttendantButtonAction.RESOURCE_ACCOUNT: {
        if (!btn.entity) {
          return ZiroFormsValidationState.INVALID;
        }
        const result = this._entityValidationResults.get(btn.key);
        if (result && (!result.hasValidLicense || !result.hasPhoneNumber)) {
          return ZiroFormsValidationState.WARNING;
        }
        return ZiroFormsValidationState.VALID;
      }
      case AutoAttendantButtonAction.VOICE_APP:
      case AutoAttendantButtonAction.VOICEMAIL:
        return btn.entity && btn.entity.status !== MicrosoftTeamsCallQueueViewEntityStatus.GUID_NOT_FOUND
          ? ZiroFormsValidationState.VALID
          : ZiroFormsValidationState.INVALID;
      case AutoAttendantButtonAction.EXTERNAL_PHONE_NUMBER:
        return btn.destination && E164_REGEX.test(btn.destination)
          ? ZiroFormsValidationState.VALID
          : ZiroFormsValidationState.INVALID;
      case AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE:
        return btn.audioInfo || btn.audioFile || btn.audioFileId
          ? ZiroFormsValidationState.VALID
          : ZiroFormsValidationState.INVALID;
      case AutoAttendantButtonAction.ANNOUNCEMENT_TTS:
        return btn.destination ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
      default:
        return ZiroFormsValidationState.VALID;
    }
  }

  private _expandAndFocusInvalidKey(invalidKey: string): void {
    if (this.unusedKeys.some(({ key }) => key === invalidKey)) {
      this.unusedOpen.set(true);
    }
    this._expandedKey.set(invalidKey);
    setTimeout(() => {
      this.rowForms?.find((form) => form.buttonKey() === invalidKey)?.triggerSubmitValidation();
    });
  }

  private _syncButtons(): void {
    const keys = this.dialKeys() ?? {};
    const existing = this.buttons();
    const configured = ALL_BUTTON_DEFS.flatMap(({ button, key }) => {
      const btn = keys[button];
      if (!btn?.action) {
        return [];
      }
      const existingBtn = existing.find((b) => b.key === key);
      const raw: UiDialButton = {
        key,
        button: button,
        action: btn.action,
        destination: btn.destination ?? '',
        audioFileId:
          btn.action === AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE ? (btn.destination ?? null) : null,
        label: '',
        icon: '',
        entity: ('entity' in btn ? btn.entity : null) ?? existingBtn?.entity ?? null,
        audioInfo: btn.audioInfo ?? null,
        voiceCommand: btn.voiceCommand,
      };
      return [this._withResolvedDisplay(raw)];
    });

    this.buttons.set(configured);
  }

  private _updateButtonValidity(update: ZiroFormsUpdate<MenuButtonRowEntity>): void {
    this._buttonValidity.update((current) => ({
      ...current,
      [update.new.key]: update.valid ?? ZiroFormsValidationState.VALID,
    }));
  }

  private _shouldUpdateButton(update: ZiroFormsUpdate<MenuButtonRowEntity>): boolean {
    return !!update.new && !!update.old && !isEqual(update.old, update.new);
  }

  private _resolveAudioFile(update: MenuButtonRowEntity): File {
    if (update.action === AutoAttendantButtonAction.ANNOUNCEMENT_TTS) {
      return null;
    }

    if (update.audioFile instanceof File) {
      return update.audioFile;
    }

    const existingButton = this.buttons().find((button) => button.key === update.key);
    return existingButton?.audioFile instanceof File ? existingButton.audioFile : null;
  }

  private _updateButtons(
    current: UiDialButton[],
    update: MenuButtonRowEntity,
    resolvedAudioFile: File | null,
  ): UiDialButton[] {
    const existing = current.find((button) => button.key === update.key);

    if (existing) {
      return current.map((button) =>
        button.key === update.key ? this._mergeExistingButton(button, update, resolvedAudioFile) : button,
      );
    }

    const promotedButton = this._createPromotedButton(update, resolvedAudioFile);
    return promotedButton ? [...current, promotedButton] : current;
  }

  private _mergeExistingButton(
    button: UiDialButton,
    update: MenuButtonRowEntity,
    resolvedAudioFile: File,
  ): UiDialButton {
    const isDoNothing = update.action === null;
    const isTts = update.action === AutoAttendantButtonAction.ANNOUNCEMENT_TTS;
    const merged: UiDialButton = isDoNothing
      ? {
          ...button,
          action: null,
          destination: '',
          entity: null,
          audioFile: null,
          audioInfo: null,
          audioFileId: null,
          voiceCommand: update.voiceCommand,
        }
      : {
          ...button,
          action: update.action,
          destination: update.destination,
          entity: update.entity ?? null,
          audioFile: resolvedAudioFile,
          audioInfo: isTts ? null : button.audioInfo,
          audioFileId: isTts ? null : resolvedAudioFile instanceof File ? null : (button.audioFileId ?? null),
          voiceCommand: update.voiceCommand,
        };

    return this._withResolvedDisplay(merged);
  }

  private _createPromotedButton(update: MenuButtonRowEntity, resolvedAudioFile: File | null): UiDialButton | null {
    const def = ALL_BUTTON_DEFS.find((buttonDef) => buttonDef.key === update.key);
    if (!def || !update.action) {
      return null;
    }

    const promoted: UiDialButton = {
      key: def.key,
      button: def.button,
      action: update.action,
      destination: update.destination ?? '',
      audioFileId: null,
      label: '',
      icon: '',
      entity: update.entity ?? null,
      audioInfo: null,
      voiceCommand: update.voiceCommand,
      audioFile: resolvedAudioFile,
    };

    return this._withResolvedDisplay(promoted);
  }

  private _withResolvedDisplay(button: UiDialButton): UiDialButton {
    const display = this._teamsAutoAttendantService.resolveButtonDisplay(button);
    return {
      ...button,
      label: display.label,
      destination: display.destination,
      icon: display.icon,
      subLabel: display.subLabel,
    };
  }

  protected getTtsDisplayText(destination: string): string {
    const stripped = destination.replace(/\s+/g, ' ').trim();
    return stripped.length > 255 ? stripped.substring(0, 255) + '...' : stripped;
  }

  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ButtonSizes = ButtonSizes;
  protected readonly ButtonStyles = ButtonStyles;
  protected readonly AutoAttendantButtonAction = AutoAttendantButtonAction;
}
